/* Copyright (C) mroads, LLC - All Rights Reserved
 * Unauthorized copying of this file, via any medium is strictly prohibited
 * Proprietary and confidential
 * Written by Premsagar Choudhary <premsagar.choudhary@mroads.com>, December 2020
 * @format
 * @flow
 */

import React from 'react';
import { connect } from 'react-redux';
import View from './views';
import MenuUtils from '../../util/menu-utils';
import { shapeIdEnum, TmsComponents } from '../../enums';
import API from '../../requests';
import { getPopupConfig } from "../../components/Popup";
import FireboardUtils from '../../util/fireboard-util';

class Playground extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      popup: undefined,
      shapes: new Map(),
      selectedShape: null,
      selectedSensorGrpId: -1,
    };
    this.stationsSeatsMap = new Map();
    this.sensorGroupWithTableMap = new Map();
    this.isShapeModified = false;
    this.seatCapacity = -1;
    this.stationsArray = [];
    this.storeLayoutBySensorGroup = false;
    this.labels = {};
  };

  componentDidMount() {
    const {
      history,
      reduxProps: { storeInfo, deviceInfo, labelsData },
    } = this.props;
    if (!storeInfo.data || !deviceInfo.deviceExternalId) {
      history.goBack();
      return;
    }

    this.labels = labelsData?.labels || {};
    const { serverAppFeatures: { storeLayoutBySensorGroup } } = storeInfo?.data;
    this.storeLayoutBySensorGroup = storeLayoutBySensorGroup;
    if (this.storeLayoutBySensorGroup) {
      this.buildSensorGroupWithTableMap();
    } else {
      this.getStoreLayout(storeInfo?.data?.id);
      this.buildStationsSeatsMap();
    }
  };

  UNSAFE_componentWillReceiveProps(newProps) {
    if (this.props?.reduxProps?.labelsData !== newProps?.reduxProps?.labelsData) {
      this.labels = newProps?.reduxProps?.labelsData?.labels || {};
    }
  }

  buildStationsSeatsMap = () => {
    const { reduxProps: { storeInfo } } = this.props;
    const stations = storeInfo?.data?.stations || [];
    let stationsSeatsMap = new Map();
    stations.forEach(station => {
      const seats = station.seats;
      if (stationsSeatsMap.has(seats)) {
        const existingStations = stationsSeatsMap.get(seats);
        stationsSeatsMap.set(seats, [...existingStations, station]);
      } else {
        stationsSeatsMap.set(seats, [station]);
      }
    });
    this.stationsSeatsMap = stationsSeatsMap;
  };

  getStoreLayout = storeId => {
    this.getSpinnerPopup('spinner', this.labels?.DESIGNER_FETCH_STORE_LAYOUT, null);
    API.getStoreLayout(storeId)
      .then(response => {
        let { layoutJson } = response.data;
        this.parseLayoutJson(layoutJson);
      })
      .catch(error => this.handleAPIErrors(error));
  };

  parseLayoutJson = (layoutJson) => {
    let shapes = new Map();
    try {
      const parsedShapes = JSON.parse(layoutJson);
      if (parsedShapes && Object.keys(parsedShapes).length !== 0 && parsedShapes.constructor === Object) {
        shapes = new Map(Object.entries(parsedShapes));
      }
    } catch (error) {
      console.error('error in parsing layout json - ', error);
    }
    this.setState({ shapes, popup: undefined });
  };

  validateBeforeDrawShape = () => {
    let valid = true;
    if (this.storeLayoutBySensorGroup && this.state.selectedSensorGrpId === -1) {
      this.getPopup('confirmation', this.labels?.DESIGNER_CONFIRMATION, this.labels?.DESIGNER_SELECT_SENSOR_GROUP_DRAW);
      valid = false;
    }
    return valid;
  };

  initShape = (type, seatCapacity) => {
    if (!this.validateBeforeDrawShape()) {
      return;
    }

    let { id, shapeProps } = this.getBasicProperty();
    if (type === TmsComponents.circle) {
      shapeProps = {
        ...shapeProps,
        shapeId: shapeIdEnum.CIRCLE,
        width: 40,
        height: 40,
      };
    } else if (type === TmsComponents.rectangle) {
      shapeProps = {
        ...shapeProps,
        shapeId: shapeIdEnum.RECTANGLE,
        width: 80,
        height: 40,
      };
    } else if (type === TmsComponents.square) {
      shapeProps = {
        ...shapeProps,
        shapeId: shapeIdEnum.SQUARE,
        width: 40,
        height: 40,
      };
    } else if (type === TmsComponents.polygon) {
      shapeProps = {
        ...shapeProps,
        shapeId: shapeIdEnum.POLYGON,
        sides: 6,
        height: 120,
        width: 120,
      };
    } else if (
      type === TmsComponents.chair ||
      type === TmsComponents.door ||
      type === TmsComponents.window ||
      type === TmsComponents.sofa ||
      type === TmsComponents.aquarium ||
      type === TmsComponents.kitchen ||
      type === TmsComponents.painting ||
      type === TmsComponents.restroom
    ) {
      const shapeId = this.getShapeId(type);
      shapeProps = {
        ...shapeProps,
        shapeId: shapeId,
        height: 100,
        width: 100,
      };
    }

    this.isShapeModified = true;
    let { shapes, selectedShape } = this.state;
    selectedShape = shapeProps; //set initial transformer
    shapes.set(id, shapeProps);
    this.setState({ shapes, selectedShape });
  };

  onSelectStation = (station, shapeId) => {
    let { id, shapeProps } = this.getBasicProperty();
    shapeProps = {
      ...shapeProps,
      stationId: station.id,
      stationNumber: station.number,
      shapeId: shapeId,
      seatCapacity: station.seats,
      scaleX: 0.3,
      scaleY: 0.3,
    };

    this.isShapeModified = true;
    let { selectedShape, shapes } = this.state;
    selectedShape = shapeProps; //set initial transformer
    shapes.set(id, shapeProps);
    this.setState({ shapes, selectedShape });
  };

  getBasicProperty = () => {
    const id = MenuUtils.generateUniqueId();
    let shapeProps = {
      stationId: null,
      stationNumber: '',
      shapeId: null,
      seatCapacity: -1,
      id,
      x: 100,
      y: 100,
      rotation: 0,
      stroke: '#ffffff',
      strokeWidth: 1,
      fill: '',
    };
    return { id, shapeProps };
  };

  getShapeId = type => {
    switch (type) {
      case TmsComponents.chair: return shapeIdEnum.CHAIR_IMG;
      case TmsComponents.door: return shapeIdEnum.DOOR_IMG;
      case TmsComponents.window: return shapeIdEnum.WINDOW_IMG;
      case TmsComponents.sofa: return shapeIdEnum.SOFA_IMG;
      case TmsComponents.aquarium: return shapeIdEnum.AQUARIUM_IMG;
      case TmsComponents.kitchen: return shapeIdEnum.KITCHEN_IMG;
      case TmsComponents.painting: return shapeIdEnum.PAINTING_IMG;
      case TmsComponents.restroom: return shapeIdEnum.RESTROOM_IMG;
      default: return null;
    }
  };

  getAssignedAndUnAssignedTables = (seatCapacity) => {
    let tablesWithSeatCapacity = [];
    if (this.storeLayoutBySensorGroup) {
      const { selectedSensorGrpId } = this.state;
      // this.sensorGroupWithTableMap: new Map()<sensorGroupId, new Map()<seatCapacity, Array of stations>>
      const tablesWithSeatsMap = this.sensorGroupWithTableMap.has(selectedSensorGrpId)
        ? this.sensorGroupWithTableMap.get(selectedSensorGrpId)
        : new Map();
      // tablesWithSeatsMap: new Map()<seatCapacity, Array of stations>
      tablesWithSeatCapacity = tablesWithSeatsMap.has(seatCapacity)
        ? tablesWithSeatsMap.get(seatCapacity)
        : [];
    } else {
      tablesWithSeatCapacity = this.stationsSeatsMap.get(seatCapacity);
    }

    return this.filterAssignedAndUnAssignedTbl(tablesWithSeatCapacity);
  };

  filterAssignedAndUnAssignedTbl = (tablesWithSeatCapacity) => {
    const { shapes } = this.state;
    const shapeArr = shapes instanceof Map ? Array.from(shapes.values()) : [];
    const assignedTables = [];
    const unAssignedTables = [];
    if (tablesWithSeatCapacity && Array.isArray(tablesWithSeatCapacity)) {
      tablesWithSeatCapacity.forEach(eachStation => {
        const doExist = shapeArr.find(shape => shape?.stationId && shape.stationId === eachStation.id);
        if (doExist) {
          assignedTables.push(eachStation);
        } else {
          unAssignedTables.push(eachStation);
        }
      });
    }
    return {
      assignedTables,
      unAssignedTables,
    };
  };

  getAllTablesInfo = () => {
    let seatsMap = new Map(); //new Map()<seatCapacity, Array of stations>
    if (this.storeLayoutBySensorGroup) {
      const { selectedSensorGrpId } = this.state;
      // this.sensorGroupWithTableMap: new Map()<sensorGroupId, new Map()<seatCapacity, Array of stations>>
      seatsMap = this.sensorGroupWithTableMap.has(selectedSensorGrpId)
        ? this.sensorGroupWithTableMap.get(selectedSensorGrpId)
        : new Map();
    } else {
      seatsMap = this.stationsSeatsMap;
    }

    let totalAssignedTables = 0;
    let totalTables = 0;
    let tablesInfo = {};

    for (let [seatCapacity, tables] of seatsMap) {
      if ([1, 2, 4, 6, 8, 10].includes(seatCapacity)) {
        const { assignedTables, unAssignedTables } = this.filterAssignedAndUnAssignedTbl(tables);
        const assignedCount = +assignedTables.length;
        const unAssignedCount = +unAssignedTables.length;
        const total = assignedCount + unAssignedCount;
        totalAssignedTables += assignedCount;
        totalTables += total;
        const assignedKey = `assignedTablesOfSeatCapacity_${seatCapacity}`;
        const unAssignedKey = `unAssignedTablesOfSeatCapacity_${seatCapacity}`;
        tablesInfo = {
          ...tablesInfo,
          [assignedKey]: assignedTables,
          [unAssignedKey]: unAssignedTables,
        }
      }
    }
    tablesInfo = {
      ...tablesInfo,
      totalTables,
      totalAssignedTables,
    };
    return tablesInfo;
  };

  handleAPIErrors = error => {
    const errorMsg = error.response
      ? error.response.data
      : error.message;
    const popup = getPopupConfig("error", this.closePopup);
    popup.message = errorMsg;
    this.setState({ popup });
  };

  getPopup = (type, heading, message, callback) => {
    const popup = getPopupConfig(type, () => {
      callback && callback();
      this.setState({ popup: undefined });
    });
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  getSpinnerPopup = (type, heading, message, callback) => {
    const popup = getPopupConfig('spinner');
    if (heading) {
      popup.heading = heading;
    }
    if (message) {
      popup.message = message;
    }
    this.setState({ popup });
  };

  closePopup = () => {
    this.setState({ popup: undefined });
  };

  onSelectShape = selectedShape => {
    this.setState({ selectedShape });
  };

  onStageClicked = e => {
    //deselect when clicked on empty area
    const clickedOnEmpty = e.target === e.target.getStage();
    if (clickedOnEmpty) this.onSelectShape(null);
  };

  onDeleteShape = () => {
    let { shapes, selectedShape } = this.state;
    if (selectedShape && selectedShape?.id) {
      shapes.delete(selectedShape.id);
      selectedShape = null;
      this.setState({ shapes, selectedShape });
    }
  };

  onDeleteLayout = () => {
    const popup = getPopupConfig('confirmation');
    popup.heading = this.labels?.DESIGNER_CONFIRMATION;
    popup.message = this.labels?.DESIGNER_DELETE_LAYOUT;
    popup.buttons = [
      {
        text: this.labels?.DESIGNER_YES,
        onClick: () => {
          this.isShapeModified = true;
          this.setState({
            selectedShape: null,
            shapes: new Map(),
            popup: undefined,
          });
        },
      },
      {
        text: this.labels?.DESIGNER_CANCEL,
        onClick: () => this.closePopup(),
      },
    ];
    this.setState({ popup });
  };

  onChangeShape = updatedShape => {
    let { shapes, selectedShape } = this.state;
    if (shapes.has(updatedShape?.id)) {
      shapes.set(updatedShape.id, updatedShape);
      if (selectedShape) {
        selectedShape = updatedShape;
      }
      this.setState({ shapes, selectedShape });
      this.isShapeModified = true;
    }
  };

  validateLayoutBeforeSave = () => {
    let valid = true;
    //if flag storeLayoutBySensorGroup is enabled - at least one sensor group should selected to save layout
    if (this.storeLayoutBySensorGroup && this.state.selectedSensorGrpId === -1) {
      this.getPopup('confirmation', this.labels?.DESIGNER_WARNING, this.labels?.DESIGNER_SELECT_SENSOR_GROUP_SAVE);
      valid = false;
    }
    return valid;
  };

  getPayloadForUpdateLayout = () => {
    const { reduxProps } = this.props;
    const { storeInfo } = reduxProps;
    const { data: { id: storeId } } = storeInfo || {};
    const { shapes, selectedSensorGrpId } = this.state;
    let layoutJson = {};
    shapes && shapes.forEach((value, key) => layoutJson[key] = value);
    let payload = {
      storeId,
      layoutJson: JSON.stringify(layoutJson),
    };
    if (this.storeLayoutBySensorGroup) { //if flag is enabled - append sensorGroupId to payload
      payload = {
        ...payload,
        sensorGroupId: selectedSensorGrpId,
      };
    }
    return payload;
  };

  onSaveLayout = (flag) => {
    if (this.validateLayoutBeforeSave()) {
      const callback = () => {
        const payload = this.getPayloadForUpdateLayout();
        this.getSpinnerPopup('spinner', this.labels?.DESIGNER_UPDATE_STORE_LAYOUT, null);
        let method = '';
        if (this.storeLayoutBySensorGroup) {
          method = 'updateStoreLayoutBySensorGroup';
        } else {
          method = 'updateStoreLayout';
        }
        API[method](payload)
          .then(_ => {
            this.isShapeModified = false;
            this.setState({
              selectedShape: null,
              popup: undefined,
            });
            if (flag === 'closeAfterSave') {
              this.onCloseLayout()
            };
          })
          .catch(error => this.handleAPIErrors(error));
      };

      //show confirmation popup
      const popup = getPopupConfig('confirmation');
      popup.heading = this.labels?.DESIGNER_CONFIRMATION;
      popup.message = this.labels?.DESIGNER_CLOSE_LAYOUT;
      popup.buttons = [
        {
          text: this.labels?.DESIGNER_YES,
          onClick: () => callback && callback(),
        },
        {
          text: this.labels?.DESIGNER_CANCEL,
          onClick: () => {
            if (flag === 'closeAfterSave')
              this.onCloseLayout();
            else
              this.closePopup();
          },
        },
      ];
      this.setState({ popup });
    }
  };

  closeLayoutEditor = () => {
    if (this.isShapeModified) { //show alert if there is any change and user try to close layout builder
      this.isShapeModified = false;
      this.onSaveLayout('closeAfterSave');
    } else { //close layout builder
      this.onCloseLayout();
    }
  };

  onCloseLayout = () => {
    const { history } = this.props;
    history && history.goBack();
  };

  onChangeSensorGroup = (e) => {
    const newId = +e.target.value;
    this.setState({ selectedSensorGrpId: newId }, () => {
      this.getStoreLayoutBySensorGroupId();
    });
  };

  getStoreLayoutBySensorGroupId = () => {
    const { selectedSensorGrpId } = this.state;
    //call endpoint only if sensor group is selected
    if (selectedSensorGrpId !== -1) {
      const { reduxProps } = this.props;
      const { storeInfo } = reduxProps;
      const { id: storeId } = storeInfo?.data || {};
      this.getSpinnerPopup('spinner', this.labels?.DESIGNER_FETCH_STORE_LAYOUT, null);
      API.getStoreLayoutBySensorGroup(storeId, selectedSensorGrpId)
        .then(response => {
          const { layoutJson } = response.data;
          this.parseLayoutJson(layoutJson);
        })
        .catch(error => this.handleAPIErrors(error));
    } else { //if no sensor group is selected - reset shapes and selectedShape
      this.setState({
        selectedShape: null,
        shapes: new Map(),
      });
    }
  };

  buildSensorGroupWithTableMap = () => {
    const { reduxProps } = this.props;
    const { storeInfo } = reduxProps;
    const { sensorGroups, sensors, stations = [] } = storeInfo?.data || {};
    // new Map()<sensorGroupId, Array of sensors with same sensorGroupId>
    const sensorsMap = FireboardUtils.buildSensorsHashTable(sensors);

    const sensorGroupWithTableMap = new Map();
    if (sensorGroups && Array.isArray(sensorGroups) && sensorGroups.length > 0) {
      sensorGroups.forEach(sensorGrp => {
        const sensors = sensorsMap.has(sensorGrp.id) ? sensorsMap.get(sensorGrp.id) : [];
        let filteredStations = [];
        sensors.forEach(sensor => {
          const stationArr = stations.filter(station => station.id === sensor.stationId);
          filteredStations = [...filteredStations, ...stationArr];
        });

        const tablesWith1Seat = filteredStations.filter(st => st.seats === 1);
        const tablesWith2Seat = filteredStations.filter(st => st.seats === 2);
        const tablesWith4Seat = filteredStations.filter(st => st.seats === 4);
        const tablesWith6Seat = filteredStations.filter(st => st.seats === 6);
        const tablesWith8Seat = filteredStations.filter(st => st.seats === 8);
        const tablesWith10Seat = filteredStations.filter(st => st.seats === 10);
        //new Map()<seatCapacity, Array of tables>
        const tablesWithSeatsMap = new Map([
          [1, tablesWith1Seat],
          [2, tablesWith2Seat],
          [4, tablesWith4Seat],
          [6, tablesWith6Seat],
          [8, tablesWith8Seat],
          [10, tablesWith10Seat],
        ]);
        //new Map<sensorGrouId, new Map()<seatCapacity, Array of tables>>
        sensorGroupWithTableMap.set(sensorGrp.id, tablesWithSeatsMap);
      });
    }
    this.sensorGroupWithTableMap = sensorGroupWithTableMap;
  };

  get viewProps() {
    const {
      popup,
      shapes,
      selectedShape,
      selectedSensorGrpId,
    } = this.state || {};
    return {
      popup,
      shapes,
      selectedShape,
      seatCapacity: this.seatCapacity,
      stationsArray: this.stationsArray,
      selectedSensorGrpId: selectedSensorGrpId,
      storeLayoutBySensorGroup: this.storeLayoutBySensorGroup,
      closePopup: this.closePopup,
      initShape: this.initShape,
      onDeleteShape: this.onDeleteShape,
      onDeleteLayout: this.onDeleteLayout,
      onChangeShape: this.onChangeShape,
      selectShape: this.onSelectShape,
      onStageClicked: this.onStageClicked,
      onSelectStation: this.onSelectStation,
      onSave: this.onSaveLayout,
      closeLayoutEditor: this.closeLayoutEditor,
      onChangeSensorGroup: this.onChangeSensorGroup,
      getAssignedAndUnAssignedTables: this.getAssignedAndUnAssignedTables,
      getAllTablesInfo: this.getAllTablesInfo,
    };
  };

  render() {
    const viewProps = this.viewProps;
    return <View {...viewProps} />;
  };
};

const mapStateToProps = (reduxProps) => ({
  reduxProps,
});

export default connect(mapStateToProps, null)(Playground);
